import React, { useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import { useUserDetails } from 'api/useUserDetails';
import { Offer, Portal } from 'common/contracts';
import { EFeatureFlag, OfferType } from 'constants/enums';
import { useGamePortalPagesState } from 'state/hooks/game-portal-pages.hook';
import { EToasterStatus, showToast } from 'utils/showToast';
import { z } from 'zod';

import Button from 'components/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from 'components/ui/dropdown-menu';

import {
  EBlockType,
  EContentType,
  EPagesVisibilityOptions,
  IBlock
} from '../constants';
import { camelCaseToTitleCase, mapBlockTypeToIcon } from '../utils';
import { pageFormSchema } from '../validation-schema';

const mapBlockTypeToDefaultBlock: Record<EBlockType, IBlock> = {
  [EBlockType.IMAGE]: {
    type: EBlockType.IMAGE,
    itemsPerRow: 2,
    items: [
      {
        type: EBlockType.IMAGE,
        url: '',
        visibility: EPagesVisibilityOptions.PUBLIC,
        segments: []
      }
    ]
  },
  [EBlockType.VIDEO]: {
    type: EBlockType.VIDEO,
    itemsPerRow: 2,
    items: [
      {
        type: EBlockType.VIDEO,
        url: '',
        autoplay: false,
        visibility: EPagesVisibilityOptions.PUBLIC,
        segments: []
      }
    ]
  },
  [EBlockType.CTA]: {
    type: EBlockType.CTA,
    itemsPerRow: 2,
    items: [
      {
        type: EBlockType.CTA,
        redirectUrl: '',
        visibility: EPagesVisibilityOptions.PUBLIC,
        segments: []
      }
    ]
  },
  [EBlockType.CONTENTS_PREVIEW]: {
    type: EBlockType.CONTENTS_PREVIEW,
    contentType: EContentType.ARTICLE,
    title: '',
    maxItems: 0,
    itemsPerRow: 2,
    visibility: EPagesVisibilityOptions.PUBLIC,
    segments: []
  },
  [EBlockType.HTML]: {
    type: EBlockType.HTML,
    html: '',
    itemsPerRow: 2,
    visibility: EPagesVisibilityOptions.PUBLIC,
    segments: []
  },
  [EBlockType.DAILY_BONUS]: {
    type: EBlockType.DAILY_BONUS,
    itemsPerRow: 2,
    isStoreRedirect: false,
    segments: []
  },
  [EBlockType.PROGRESS_BAR]: {
    type: EBlockType.PROGRESS_BAR,
    itemsPerRow: 2,
    isStoreRedirect: false,
    segments: []
  },
  [EBlockType.OFFER]: {
    type: EBlockType.OFFER,
    offerType: OfferType.BUNDLE,
    offerIds: [],
    itemsPerRow: 2,
    visibility: EPagesVisibilityOptions.SEGMENTED,
    segments: []
  }
};

interface IDropdownMenuItemWithIconProps {
  icon: string;
  type: EBlockType;
  handleClick: () => void;
}

const DropdownMenuItemWithIcon: React.FC<IDropdownMenuItemWithIconProps> = ({
  icon,
  type,
  handleClick
}) => {
  return (
    <DropdownMenuItem
      onClick={handleClick}
      className="text-base-popoverForeground text-sm hover:bg-base-accent cursor-pointer"
    >
      <FontAwesomeIcon icon={icon} className="text-xs" />
      {camelCaseToTitleCase(type)}
    </DropdownMenuItem>
  );
};

export const AddBlock: React.FC = () => {
  const { watch, setValue, trigger } =
    useFormContext<z.infer<typeof pageFormSchema>>();
  const [hasStoreOffers, setHasStoreOffers] = useState(false);

  const { setOpenBlockIndex, setOpenItemIndex, setStoreOffers } =
    useGamePortalPagesState();
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });

  const { portalId } = useParams();

  const { userDetails } = useUserDetails();

  const portalData: any = userDetails?.portals?.find(
    (portal: Portal) => portal.portalId === portalId
  );

  const storePublisherId = portalData?.stores?.[0]?.storeId;

  const { fetchAllOffers } = useOffers({
    shouldIgnorePagination: true,
    customPublisherId: storePublisherId
  });

  const isOfferBlockFeatureEnabled = useMemo(() => {
    return featureFlags?.[EFeatureFlag.DASHBOARD_MANAGE_BLOCKS_OFFERS_BLOCK];
  }, [featureFlags]);

  useEffect(() => {
    if (!storePublisherId || !isOfferBlockFeatureEnabled) {
      setHasStoreOffers(false);
      return;
    }

    const fetchAllOffersData = async () => {
      try {
        const [bundleOffers, specialOffers, rollingOffers] = await Promise.all([
          fetchAllOffers({
            offerType: OfferType.BUNDLE,
            fetchOnlyActiveOffers: false
          }),
          fetchAllOffers({
            offerType: OfferType.SPECIAL_OFFER,
            fetchOnlyActiveOffers: false
          }),
          fetchAllOffers({
            offerType: OfferType.ROLLING_OFFER,
            fetchOnlyActiveOffers: false
          })
        ]);

        const bundlesOffers: Offer[] = bundleOffers.filter(
          (offer) => !!offer.offerId
        );

        const specialOffersOffers: Offer[] = specialOffers.filter(
          (offer) => !!offer.offerId
        );

        const rollingOffersOffers: Offer[] = rollingOffers.filter(
          (offer) => !!offer.offerId
        );

        if (
          bundlesOffers.length > 0 ||
          specialOffersOffers.length > 0 ||
          rollingOffersOffers.length > 0
        ) {
          setHasStoreOffers(true);

          setStoreOffers({
            [OfferType.BUNDLE]: bundlesOffers,
            [OfferType.SPECIAL_OFFER]: specialOffersOffers,
            [OfferType.ROLLING_OFFER]: rollingOffersOffers
          });
        } else {
          setHasStoreOffers(false);
        }
      } catch (error) {
        console.error('Error fetching all offers:', error);
        showToast({
          message: 'Failed to load offers',
          status: EToasterStatus.ERROR
        });
        setHasStoreOffers(false);
      }
    };

    fetchAllOffersData();
  }, [storePublisherId, isOfferBlockFeatureEnabled]);

  const blockTypes = useMemo(() => {
    return Object.values(EBlockType).filter(
      (blockType) => blockType !== EBlockType.OFFER || hasStoreOffers
    );
  }, [hasStoreOffers]);

  const handleAddBlock = async (type: EBlockType) => {
    const currentBlocks = (watch('blocksV2') as IBlock[]) || [];
    const newBlock: IBlock = mapBlockTypeToDefaultBlock[type];
    const updatedBlocks = [...currentBlocks, newBlock];
    const newBlockIndex = updatedBlocks.length - 1;

    setValue('blocksV2', updatedBlocks, {
      shouldValidate: true,
      shouldDirty: true
    });

    setOpenBlockIndex(newBlockIndex);

    if (
      'items' in newBlock &&
      newBlock.items?.length &&
      newBlock.items.length > 0
    ) {
      setOpenItemIndex(`${newBlockIndex}-0`);
    }
    await trigger(`blocksV2.${newBlockIndex}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-plus" />}
          className="w-[190px]"
          type="button"
        >
          Add New Block
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="bg-base-popover border-base-border rounded-md w-auto min-w-[190px]"
      >
        {blockTypes.map((blockType) => (
          <DropdownMenuItemWithIcon
            key={blockType}
            icon={mapBlockTypeToIcon[blockType]}
            type={blockType}
            handleClick={() => handleAddBlock(blockType)}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
